import { BoardLookup } from "models/BoardLookup";
import { BoardModel } from "models/BoardModel";
import { BoardSettingsModel } from "models/BoardSettingsModel";
import { addFailure, ValidationResult } from "models/ValidationResult";
import { TranslationLabels } from "translations/TranslationLabels";

export type BoardObject = BoardModel | BoardSettingsModel | BoardLookup;
export type BoardType = string | number | BoardObject | undefined;

export function getBoardTitle(i18n: TranslationLabels, s: string) {
  s = s.trim();
  return i18n.Page_Board_Title.replace("{0}", s);
}

export function resolvePreferredName(board: BoardType) {
  if (!board) return "";
  const boardObject: BoardObject = board as BoardObject;
  if (boardObject) return getPreferredName(boardObject);
  return getBoardRouteKey(board);
}

export function getPreferredName(boardObject: BoardObject) {
  if (!boardObject) return "";
  if (boardObject.shortName) return boardObject.shortName;
  if (boardObject.longName) return boardObject.longName;
  if (boardObject.name) return boardObject.name;
  return `${boardObject.id}`;
}

export function getBoardRouteKey(board: BoardType) {
  if (!board) return undefined;
  const boardObject = typeof board === "object" ? (board as BoardObject) : undefined;
  if (boardObject) return boardObject.name || `${boardObject.id}`;
  return `${board}`;
}

function replacePlaceholders(template: string, ...values: string[]) {
  let result = template || "";
  for (let i = 0; i < values.length; i++) {
    const key = `{${i}}`;
    const value = values[i];
    result = result.includes(key) ? result.replace(key, value) : `${result}${value}`;
  }
  return result;
}

export function validateBoardId(
  boardId: string | undefined,
  i18n: TranslationLabels = window.labels || ({} as TranslationLabels)
): ValidationResult {
  i18n = i18n || ({} as TranslationLabels);

  const label = i18n.Board_Name || "Board";
  const result: ValidationResult = {
    isValid: true,
    failures: [],
  };

  if (!boardId || boardId.length === 0) {
    addFailure(
      result,
      replacePlaceholders(i18n.Validation_Field_MustNotBeEmpty, label) || `${label} must not be empty`
    );
    return result;
  }

  const s = boardId.trim();
  if (s !== boardId) {
    addFailure(result, replacePlaceholders(i18n.Validation_Field_LeadingOrTrailingWhitespace, label));
    return result;
  }

  if (s.startsWith("-")) {
    addFailure(result, replacePlaceholders(i18n.Validation_Field_StartsWithDashes, label));
    return result;
  }

  if (s.endsWith("-")) {
    addFailure(result, replacePlaceholders(i18n.Validation_Field_EndsWithDashes, label));
    return result;
  }

  if (s.includes("--")) {
    addFailure(result, replacePlaceholders(i18n.Validation_Field_ConcurrentDashes, label));
    return result;
  }

  const validCharacters = new Set("abcdefghijklmnopqrstuvwxyz0123456789-");
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i);
    if (!validCharacters.has(c)) {
      addFailure(result, replacePlaceholders(i18n.Validation_Field_InvalidCharacters, label, c, `${i + 1}`));
      return result;
    }
  }

  if (s === "boards") {
    addFailure(result, replacePlaceholders(i18n.Validation_Field_NotInValidNames, s));
    return result;
  }

  return result;
}
